import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faUserGroup, faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";
import { COMPARTILHAMENTO, adaptaAoLeitor, descreverView } from "../utils/taskViews";
import "./SalvarViewModal.css";

/**
 * Salvar view = nomear + decidir quem enxerga.
 *
 * O aviso sobre "adapta a quem ve" so aparece quando ele muda o resultado:
 * uma view compartilhada filtrada por "eu" mostra, para cada pessoa, as
 * tarefas dela. Isso surpreende se ninguem avisar.
 */
const SalvarViewModal = ({
  show,
  onHide,
  onSalvar,
  viewAtual,
  estado,
  contexto,
  salvando,
  erro,
}) => {
  const [nome, setNome] = useState("");
  const [compartilhamento, setCompartilhamento] = useState(COMPARTILHAMENTO.PRIVADA);
  const [sobrescrever, setSobrescrever] = useState(false);

  // Só dá para atualizar uma view existente se ela for sua e não for do sistema.
  const podeSobrescrever = Boolean(viewAtual && !viewAtual.sistema && viewAtual.minha);

  useEffect(() => {
    if (!show) return;

    setNome(podeSobrescrever ? viewAtual.nome : "");
    setCompartilhamento(
      podeSobrescrever ? viewAtual.compartilhamento : COMPARTILHAMENTO.PRIVADA
    );
    setSobrescrever(podeSobrescrever);
  }, [show, podeSobrescrever, viewAtual]);

  const previa = descreverView({ ...estado, filtros: estado.filtros }, contexto);
  const personalizada = adaptaAoLeitor({ filtros: estado.filtros });
  const vaiCompartilhar = Number(compartilhamento) === COMPARTILHAMENTO.WORKSPACE;

  function confirmar() {
    const nomeLimpo = nome.trim();
    if (!nomeLimpo) return;

    onSalvar({
      nome: nomeLimpo,
      compartilhamento: Number(compartilhamento),
      sobrescrever: sobrescrever && podeSobrescrever,
    });
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Salvar view</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Nome</Form.Label>
          <Form.Control
            type="text"
            value={nome}
            autoFocus
            placeholder="Ex: Entregas da semana"
            onChange={(evento) => setNome(evento.target.value)}
            onKeyDown={(evento) => {
              if (evento.key === "Enter") {
                evento.preventDefault();
                confirmar();
              }
            }}
          />
        </Form.Group>

        <div className="sv-previa">
          <span>Esta view mostra</span>
          <strong>{previa}</strong>
        </div>

        <Form.Group className="mb-3">
          <Form.Label>Quem enxerga</Form.Label>

          <div className="sv-opcoes">
            <button
              type="button"
              className={`sv-opcao ${!vaiCompartilhar ? "ativa" : ""}`}
              onClick={() => setCompartilhamento(COMPARTILHAMENTO.PRIVADA)}
            >
              <FontAwesomeIcon icon={faLock} />
              <span>
                <strong>Só eu</strong>
                <small>Fica na sua barra de views</small>
              </span>
            </button>

            <button
              type="button"
              className={`sv-opcao ${vaiCompartilhar ? "ativa" : ""}`}
              onClick={() => setCompartilhamento(COMPARTILHAMENTO.WORKSPACE)}
            >
              <FontAwesomeIcon icon={faUserGroup} />
              <span>
                <strong>O time</strong>
                <small>Todos do workspace veem; só você edita</small>
              </span>
            </button>
          </div>
        </Form.Group>

        {vaiCompartilhar && personalizada && (
          <Alert variant="info" className="sv-aviso">
            <FontAwesomeIcon icon={faWandMagicSparkles} />
            <div>
              <strong>Esta view se adapta a quem abre.</strong>
              <span>
                Como o filtro de responsável está em "eu", cada pessoa do time vai ver
                as tarefas dela — não as suas.
              </span>
            </div>
          </Alert>
        )}

        {podeSobrescrever && (
          <Form.Check
            type="radio"
            id="sv-sobrescrever"
            name="sv-destino"
            label={`Atualizar "${viewAtual.nome}"`}
            checked={sobrescrever}
            onChange={() => setSobrescrever(true)}
          />
        )}
        {podeSobrescrever && (
          <Form.Check
            type="radio"
            id="sv-nova"
            name="sv-destino"
            label="Criar uma view nova"
            checked={!sobrescrever}
            onChange={() => setSobrescrever(false)}
          />
        )}

        {!podeSobrescrever && viewAtual && !viewAtual.minha && (
          <Form.Text muted>
            Você está numa view de {viewAtual.autor || "outra pessoa"}. Salvar cria uma
            cópia sua, sem alterar a original.
          </Form.Text>
        )}

        {erro && <Alert variant="danger" className="mt-3 mb-0 py-2">{erro}</Alert>}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={salvando}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={confirmar} disabled={salvando || !nome.trim()}>
          {salvando ? "Salvando..." : "Salvar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SalvarViewModal;
